import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Shield, Check, Clock, Lock, FileText, Search, Award, MessageSquare,
  MapPin, Calendar, ArrowRight, Mail, AlertCircle, Sparkles, Bell, Send,
  Phone as PhoneIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#D4AF37';

// Stage progression — kept lean (5 visible milestones).  Backend stages map down
// to these for the investor view; admin sees the full 9-stage pipeline.
const VISIBLE_STAGES = [
  { id: 'brief',     label: 'Brief Received', desc: 'We have your submission.', icon: FileText,
    matches: ['new', 'contacted', 'brief_confirmed'] },
  { id: 'deposit',   label: 'Search Activated', desc: 'Refundable deposit received.', icon: Lock,
    matches: ['deposit_paid'] },
  { id: 'sourcing',  label: 'Sourcing Live', desc: 'Our team is reviewing opportunities for you.', icon: Search,
    matches: ['sourcing'] },
  { id: 'presented', label: 'Deals Presented', desc: 'Qualified opportunities are ready for your review.', icon: Award,
    matches: ['presented', 'won'] },
];

const stageIndex = (status) => {
  const i = VISIBLE_STAGES.findIndex(s => s.matches.includes(status));
  return i >= 0 ? i : 0;
};

const fmtDate = (iso, opts = { day: '2-digit', month: 'short', year: 'numeric' }) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', opts); } catch { return ''; }
};

const fmtTimeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const d = Math.floor(ms / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

const InvestorBriefPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Your Bespoke Brief · Black Star PI';
  }, []);

  useEffect(() => {
    if (!token) { setError('missing'); setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/my-brief/${encodeURIComponent(token)}`);
        if (!r.ok) {
          setError(r.status === 404 ? 'not-found' : 'server');
        } else {
          setBrief(await r.json());
        }
      } catch { setError('network'); }
      setLoading(false);
    })();
  }, [token]);

  const cdInfo = useMemo(() => {
    if (!brief?.deposit_paid_at) return null;
    const start = new Date(brief.deposit_paid_at).getTime();
    if (Number.isNaN(start)) return null;
    const weeks = brief.deposit_period_weeks || 6;
    const deadline = start + weeks * 7 * 86400000;
    const diff = deadline - Date.now();
    const days = Math.ceil(diff / 86400000);
    return {
      days,
      deadline: new Date(deadline),
      total: weeks * 7,
      progress: Math.min(100, Math.max(0, (1 - diff / (weeks * 7 * 86400000)) * 100)),
      overdue: days < 0,
    };
  }, [brief]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center text-white/40 text-sm">
        Loading your brief…
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="min-h-screen bg-[#0a1a14] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="investor-error-state">
          <AlertCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-5" />
          <h1 className="text-2xl font-extralight mb-3">
            {error === 'missing' ? 'Brief link required' : 'Brief not found'}
          </h1>
          <p className="text-white/55 text-sm leading-relaxed mb-6">
            {error === 'missing' && 'This page is accessed via the secure link in your confirmation email. Please open that link again.'}
            {error === 'not-found' && 'This brief link is invalid or has been deactivated. If you think this is an error, reply to our team or submit a new brief.'}
            {(error === 'server' || error === 'network') && "We couldn't load your brief right now. Please refresh in a moment or contact our team."}
          </p>
          <Link to="/bespoke-sourcing" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#0a1a14] text-xs font-bold uppercase tracking-[0.2em]">
            Submit New Brief <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const firstName = (brief.full_name || '').split(' ')[0] || 'Investor';
  const currentIdx = stageIndex(brief.status);

  return (
    <div className="min-h-screen bg-[#0a1a14] text-white" data-testid="investor-brief-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="block w-2.5 h-2.5 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold">Black Star PI</span>
          </Link>
          <div className="hidden sm:flex items-center gap-1.5 text-[#D4AF37] text-[10px] uppercase tracking-[0.3em]">
            <Shield className="w-3.5 h-3.5" /> Private · Confidential
          </div>
        </div>
      </header>

      {/* Hero greeting */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Your Bespoke Brief</p>
        <h1 className="text-4xl md:text-5xl font-extralight tracking-tight leading-[1.1] mb-3">
          Welcome back, <span className="italic text-[#D4AF37]">{firstName}</span>.
        </h1>
        <p className="text-white/55 text-base max-w-2xl leading-relaxed">
          Track your bespoke search, review curated opportunities, and stay updated — all in one private place.
        </p>
      </section>

      {/* Pipeline Stepper */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-5">Progress</p>
        <div className="relative">
          {/* Line */}
          <div className="absolute left-5 right-5 top-5 h-px bg-white/10 hidden md:block" />
          <div
            className="absolute left-5 top-5 h-px bg-[#D4AF37] transition-all hidden md:block"
            style={{ width: `${(currentIdx / (VISIBLE_STAGES.length - 1)) * 100}%` }}
          />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-3 relative">
            {VISIBLE_STAGES.map((s, i) => {
              const done = i < currentIdx;
              const active = i === currentIdx;
              const Icn = s.icon;
              return (
                <div key={s.id} className="flex md:flex-col items-start md:items-center gap-3 md:gap-2.5 md:text-center" data-testid={`stage-${s.id}`}>
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 ${
                      done ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0a1a14]'
                        : active ? 'bg-[#0a1a14] border-[#D4AF37] text-[#D4AF37] shadow-lg shadow-[#D4AF37]/20'
                        : 'bg-[#0a1a14] border-white/15 text-white/30'
                    }`}
                  >
                    {done ? <Check className="w-5 h-5" strokeWidth={3} /> : <Icn className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 md:flex-none">
                    <p className={`text-sm font-semibold ${active ? 'text-[#D4AF37]' : done ? 'text-white' : 'text-white/40'}`}>{s.label}</p>
                    <p className="text-white/40 text-[11px] mt-0.5 md:mt-1 md:px-2 leading-snug">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status-specific note */}
        {brief.status === 'lost' && (
          <p className="mt-8 text-center text-white/45 text-sm italic">Your sourcing window has concluded. Our team will reach out about next steps.</p>
        )}
        {brief.status === 'refunded' && (
          <p className="mt-8 text-center text-white/45 text-sm italic">Your deposit has been refunded. Reach out anytime to start a fresh brief.</p>
        )}
      </section>

      {/* Countdown card */}
      {cdInfo && (
        <section className="max-w-5xl mx-auto px-6 pb-12" data-testid="investor-countdown">
          <div className="bg-gradient-to-br from-[#D4AF37]/[0.05] via-[#0a1a14] to-[#0a1a14] border border-[#D4AF37]/25 rounded-2xl p-6 md:p-8 relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-2 font-semibold flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Sourcing Window
                </p>
                <div className="flex items-baseline gap-2.5">
                  <span className={`text-4xl font-light ${cdInfo.overdue ? 'text-red-300' : 'text-[#D4AF37]'}`}>
                    {cdInfo.overdue ? `${Math.abs(cdInfo.days)}d` : `${cdInfo.days}d`}
                  </span>
                  <span className="text-white/55 text-sm">
                    {cdInfo.overdue ? 'past deadline' : 'remaining'}
                  </span>
                </div>
                <p className="text-white/40 text-[12px] mt-1.5">
                  Deadline · {fmtDate(cdInfo.deadline.toISOString())}
                </p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-white/30 text-[10px] uppercase tracking-[0.2em] mb-1">Deposit Status</p>
                <p className="text-[#D4AF37] text-sm font-semibold">✓ Paid · {fmtTimeAgo(brief.deposit_paid_at)}</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-6">
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full ${cdInfo.overdue ? 'bg-red-400' : 'bg-[#D4AF37]'} transition-all`}
                  style={{ width: `${cdInfo.progress}%` }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Curated deals */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-1">Curated for you</p>
            <h2 className="text-xl md:text-2xl font-light">Matching opportunities</h2>
          </div>
          {(brief.curated_previews || []).length > 0 && (
            <p className="text-white/40 text-[11px]">{brief.curated_previews.length} deal{brief.curated_previews.length !== 1 ? 's' : ''}</p>
          )}
        </div>

        {(brief.curated_previews || []).length === 0 ? (
          <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-xl px-6 py-10 text-center">
            <Sparkles className="w-7 h-7 text-[#D4AF37]/50 mx-auto mb-3" />
            <p className="text-white/60 text-sm mb-1">No opportunities yet — your sourcing is in progress.</p>
            <p className="text-white/35 text-[12px] max-w-md mx-auto">Our team is reviewing the private pipeline against your brief. When qualified deals are ready, you'll see them here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {brief.curated_previews.map((d) => {
              const hero = (d.gallery && d.gallery[0]) || '';
              const fee = d.finders_fee
                ? `${({ GBP: '£', EUR: '€' }[d.finders_fee_currency] || 'US$')}${Number(d.finders_fee).toLocaleString()}`
                : '';
              return (
                <Link
                  key={d.slug}
                  to={`/deals/${d.slug}`}
                  className="group bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/40 transition-all"
                  data-testid={`curated-deal-${d.slug}`}
                >
                  {hero && (
                    <div className="aspect-video overflow-hidden">
                      <img src={hero} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <p className="text-white font-semibold text-sm mb-1.5 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">{d.title}</p>
                    <div className="flex items-center gap-2 text-white/40 text-[11px] mb-3">
                      {d.location && (<><MapPin className="w-3 h-3" /> {d.location}</>)}
                      {d.asset_type && <span>· {d.asset_type}</span>}
                    </div>
                    {fee && (
                      <p className="text-[#D4AF37] text-xs font-bold tracking-wide">FROM {fee}</p>
                    )}
                    <div className="flex items-center gap-1 text-[#D4AF37] text-[11px] mt-3 font-semibold uppercase tracking-wider">
                      View brochure <ArrowRight className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Updates timeline */}
      {(brief.investor_updates || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-5">Latest updates</p>
          <div className="space-y-3">
            {[...brief.investor_updates].reverse().map((u) => (
              <div key={u.id} className="flex gap-4 bg-white/[0.02] border border-white/10 rounded-lg p-4" data-testid={`update-${u.id}`}>
                <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#D4AF37] mt-2" />
                <div className="flex-1">
                  <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{u.message}</p>
                  <p className="text-white/35 text-[11px] mt-2">{fmtDate(u.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Brief summary */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-5">Your brief at a glance</p>
        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
          {brief.full_name && <Row label="Investor" value={brief.full_name} />}
          {brief.email && <Row label="Email" value={brief.email} />}
          {brief.phone && <Row label="Phone" value={brief.phone} />}
          {brief.country_residence && <Row label="Country of residence" value={brief.country_residence} />}
          {brief.preferred_countries?.length > 0 && (
            <Row label="Target countries" value={brief.preferred_countries.join(' · ')} />
          )}
          {brief.preferred_cities && <Row label="Cities" value={brief.preferred_cities} />}
          {brief.asset_classes?.length > 0 && <Row label="Asset classes" value={brief.asset_classes.join(' · ')} />}
          {brief.strategies?.length > 0 && <Row label="Strategies" value={brief.strategies.join(' · ') + (brief.other_strategy ? ` · ${brief.other_strategy}` : '')} />}
          {brief.investment_budget && <Row label="Budget" value={brief.investment_budget} />}
          {brief.target_yield && <Row label="Yield target" value={brief.target_yield} />}
          {brief.capital_growth && <Row label="Capital growth target" value={brief.capital_growth} />}
          {brief.holding_period && <Row label="Hold period" value={brief.holding_period} />}
          {brief.timeline && <Row label="Timeline" value={brief.timeline} />}
          {brief.experience_level && <Row label="Experience" value={brief.experience_level} />}
          {brief.risk_appetite && <Row label="Risk appetite" value={brief.risk_appetite} />}
        </div>
        <p className="text-white/30 text-[11px] mt-3">
          Submitted {fmtTimeAgo(brief.created_at)} · Need a tweak? Reply to your confirmation email and our team will refine the brief with you.
        </p>
      </section>

      {/* Notification preferences */}
      <NotificationPrefs token={token} brief={brief} onUpdate={setBrief} />

      {/* Footer */}
      <footer className="border-t border-white/10 mt-8">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center">
          <p className="text-white/40 text-[11px] uppercase tracking-[0.25em] mb-2 inline-flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Need anything?
          </p>
          <p className="text-white/55 text-sm">
            Reply to your confirmation email or message us directly — your sourcer is one tap away.
          </p>
          <p className="text-white/25 text-[10px] mt-6 tracking-widest uppercase">
            Black Star PI · Confidential
          </p>
        </div>
      </footer>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-3 py-1.5">
    <span className="text-[#D4AF37]/70 text-[11px] uppercase tracking-wider min-w-[140px] font-semibold">{label}</span>
    <span className="text-white/85 text-sm flex-1">{value}</span>
  </div>
);

// ===== Notification Preferences (multi-channel investor opt-ins) =====
const NotificationPrefs = ({ token, brief, onUpdate }) => {
  const [prefs, setPrefs] = useState(brief.notify_prefs || { email: true, whatsapp: false, telegram: false });
  const [saving, setSaving] = useState(false);
  const [telegramInvite, setTelegramInvite] = useState(null);

  useEffect(() => { setPrefs(brief.notify_prefs || { email: true, whatsapp: false, telegram: false }); }, [brief]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/public/my-brief/by-token/${encodeURIComponent(token)}/notify-prefs`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (r.ok) {
        const d = await r.json();
        onUpdate({ ...brief, notify_prefs: d.notify_prefs });
      } else {
        setPrefs(prefs); // revert
      }
    } catch { setPrefs(prefs); }
    setSaving(false);
  };

  // Lazy-load the Telegram deep link once the toggle is enabled
  useEffect(() => {
    if (!prefs.telegram || telegramInvite) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/my-brief/by-token/${encodeURIComponent(token)}/telegram-invite`);
        if (r.ok) setTelegramInvite(await r.json());
      } catch { /* silent */ }
    })();
  }, [prefs.telegram, telegramInvite, token]);

  const channels = [
    {
      key: 'email',
      label: 'Email',
      desc: 'Detailed updates to your registered email address.',
      icon: Mail,
      status: 'Always available',
      meta: brief.email,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      desc: 'Short message + portal link directly on WhatsApp.',
      icon: MessageSquare,
      status: prefs.whatsapp ? 'Active' : 'Off',
      meta: brief.phone,
    },
    {
      key: 'telegram',
      label: 'Telegram',
      desc: 'Real-time push to your Telegram account.',
      icon: Send,
      status: prefs.telegram
        ? (brief.telegram_connected || telegramInvite?.connected ? 'Active · Connected' : 'Awaiting connection')
        : 'Off',
      meta: null,
    },
  ];

  return (
    <section className="max-w-5xl mx-auto px-6 pb-14" data-testid="notification-prefs">
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-1 flex items-center gap-1.5">
            <Bell className="w-3 h-3" /> Stay informed
          </p>
          <h2 className="text-xl md:text-2xl font-light">How you'd like to hear from us</h2>
        </div>
        {saving && <span className="text-white/40 text-[11px]">Saving…</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {channels.map(ch => {
          const Icn = ch.icon;
          const on = !!prefs[ch.key];
          return (
            <div
              key={ch.key}
              className={`relative p-5 rounded-xl border transition-all ${
                on
                  ? 'bg-[#D4AF37]/[0.05] border-[#D4AF37]/40'
                  : 'bg-white/[0.02] border-white/10 hover:border-white/20'
              }`}
              data-testid={`channel-${ch.key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  on ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-white/5 text-white/40'
                }`}>
                  <Icn className="w-5 h-5" />
                </div>
                <button
                  onClick={() => toggle(ch.key)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    on ? 'bg-[#D4AF37]' : 'bg-white/15'
                  }`}
                  aria-pressed={on}
                  data-testid={`channel-toggle-${ch.key}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      on ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className={`text-sm font-semibold mb-1 ${on ? 'text-white' : 'text-white/65'}`}>{ch.label}</p>
              <p className="text-white/50 text-[12px] leading-snug mb-2.5">{ch.desc}</p>
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${
                on
                  ? (ch.key === 'telegram' && !brief.telegram_connected && !telegramInvite?.connected ? 'text-yellow-300' : 'text-[#D4AF37]')
                  : 'text-white/35'
              }`}>{ch.status}</p>
              {ch.meta && (
                <p className="text-white/40 text-[11px] mt-1 truncate" title={ch.meta}>{ch.meta}</p>
              )}

              {/* Telegram connection CTA */}
              {ch.key === 'telegram' && on && !(brief.telegram_connected || telegramInvite?.connected) && telegramInvite?.invite_url && (
                <a
                  href={telegramInvite.invite_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#D4AF37] hover:underline"
                  data-testid="connect-telegram-link"
                >
                  Connect Telegram <ArrowRight className="w-3 h-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-white/30 text-[11px] mt-4">
        Email is always available. WhatsApp uses your submitted number. Telegram requires a one-time tap to authorise the BSPI bot.
      </p>
    </section>
  );
};

export default InvestorBriefPage;
